<template>
    <div class="hts-layout-default admincp">
        <template v-if="!initialized">
            <div class="w-screen h-screen">
                <div class="hts-loading loading">
                    <div class="hts-loading__dot-fire" />
                </div>
            </div>
        </template>
        <template v-else>
            <layout-navigation
                :menu-items="menuItems"
                :top-menu-items="[]"
                type-page="user">
                <router-view />
            </layout-navigation>
        </template>
    </div>
</template>

<script lang="ts" setup>
import LayoutNavigation from './-default/navigation.vue'
import menuItems from '~/data/menus/user/index.json'

import { $lodash } from '#imports'
const router = useRouter()
const { $event } = useNuxtApp()

const workspace = computed(() => store.auth.getWorkspace)

const config = useRuntimeConfig().public
useHead({
  title: config.appName
})
const route = useRoute()
const initialized = ref(false)
const { colors } = useRuntimeConfig().public
const store = $store()
const themeColor = store.theme.getColor

const setThemeColor = () => {
  if (process.client && colors[themeColor as keyof typeof colors]) {
    const color = colors[themeColor as keyof typeof colors]
    const r: HTMLElement | null = document.querySelector(':root')
    if (r) {
      r.style.setProperty('--primary-main-50', color['50'])
      r.style.setProperty('--primary-main-100', color['100'])
      r.style.setProperty('--primary-main-200', color['200'])
      r.style.setProperty('--primary-main-300', color['300'])
      r.style.setProperty('--primary-main-400', color['400'])
      r.style.setProperty('--primary-main-500', color['500'])
      r.style.setProperty('--primary-main-600', color['600'])
      r.style.setProperty('--primary-main-700', color['700'])
      r.style.setProperty('--primary-main-800', color['800'])
      r.style.setProperty('--primary-main-900', color['900'])
    }
  }
}

const getSystemConfig = async () => {
  try {
    const response = await $api($urlSystem.system.getSystemConfig)
    const responseData: any = $lodash.get(response, 'data.value')
    if (responseData?.success) {
      const configData = responseData.data || {}
      const user = configData.user || {}
      store.system.setWorkspaceUsers([])
      store.system.setCompanyUsers([])
      store.system.setCompany(null)
      store.system.setWorkspace(null)
      store.system.timezone = user.timezone
      store.system.dateFormat = user.dateFormat
      store.system.timeFormat = user.timeFormat
      store.system.timezoneText = user.timezoneText
      store.userConfig.setPage('ADMINCP')
      store.userConfig.setUser(user)
      store.userConfig.workspaces = configData.workspaces || []
      store.userConfig.isAdmin = configData.isAdmin || false
      store.userConfig.setAvatar(user.avatar)
      store.data.setBusinesses(configData.business)
      store.data.setVehicleCategories(configData.vehicleCategories)
      store.data.setTasks(configData.task)
      store.data.setTollStationStructure(configData.tollStationStructure)
    } else {
      router.push({ name: 'errors-access-denied' })
    }
  } catch (e: any) {
    if (process.client && typeof e.message === 'string') {
      $toast().error(e.message)
    }
  }
}

const getNotifications = () => {
  $api($urlSystem.notification.notificationList, {
    body: {
      startRow: 0,
      endRow: 10
    }
  })
    .then((notifications) => {
      const success = $lodash.get(notifications, 'data.value.success')
      if (success) {
        const res: any = $lodash.get(notifications, 'data.value.data')
        const notify = res?.notification || []
        const unread = res?.unread || 0
        store.notification.setNotifications(notify)
        store.notification.setUnread(unread)
      }
    })
    .catch((e: any) => {
      if (process.client && typeof e.message === 'string') {
        $toast().error(e.message)
      }
    })
}

const getSystemData = $lodash.debounce(async () => {
  try {
    const response = await $api($urlSystem.system.getSystemData)
    const responseData: any = $lodash.get(response, 'data.value')
    if (responseData.success) {
      const configData = responseData.data || {}
      store.data.setBusinesses(configData.business)
      store.data.setTasks(configData.task)
      store.data.setTollStationStructure(configData.tollStationStructure)
    }
  } catch (e: any) {
    if (process.client && typeof e.message === 'string') {
      $toast().error(e.message)
    }
  }
}, 200)

onMounted(async () => {
  const { id: workspaceId, companyType, timezone, type, cooperatorTypes } = workspace.value

  if (type === 'VENDOR' && workspaceId) {
    if (['OWNER'].includes(companyType)) {
      return router.push({
        name: 'trucker-id-dashboard',
        params: { id: workspaceId }
      })
    }
    if (['PARTNER'].includes(companyType)) {
      if (cooperatorTypes.includes('PARTNER')) {
        return router.push({
          name: 'pp-id',
          params: { id: workspaceId }
        })
      }

      if (cooperatorTypes.includes('CUSTOMER')) {
        return router.push({
          name: 'pc-id',
          params: { id: workspaceId }
        })
      }
    }
  }

  if (type === 'IMPORT_AND_EXPORT' && workspaceId) {
    if (['OWNER'].includes(companyType)) {
      return router.push({
        name: 'c-id-dashboard',
        params: { id: workspaceId }
      })
    }
  }

  await getSystemConfig()
  getNotifications()

  useState('initialized', () => true)
  themeColor !== 'orange' && setThemeColor()
  store.auth.setWorkspaceId(null)

  const user: any = $store().userConfig.user
  if (process.client) {
    await $event.connect(false)
    $event.listen('admincp-notification-' + user._id, (data: any) => {
      store.notification.unshiftNotification(data?.message)
      store.notification.plusUnread(1)
    })

    $event.listen('admincp-get-system-data-admin', async (data: any) => {
      await getSystemData()
    })
  }

  nextTick(() => {
    initialized.value = true
  })
})

onUnmounted(() => {
  store.notification.clear()
  const user: any = store.userConfig.user
  $event.unsubscribe('admincp-notification-' + user._id, () => {})
  $event.unsubscribe('admincp-get-system-data-admin', () => {})
})
</script>
